/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { Loader2, Landmark } from "lucide-react";
import { useCnPayrollAccess } from "./hrmsShared";
import { CnRunsTab } from "./cn/CnRunsTab";
import { CnProfilesTab } from "./cn/CnProfilesTab";
import { CnConfigTab } from "./cn/CnConfigTab";

const TABS = [
  { k: "runs", zh: "月度工资表", en: "Monthly Payroll" },
  { k: "profiles", zh: "员工薪资档案", en: "Salary Profiles" },
  { k: "config", zh: "缴费模板与汇率", en: "Rates & FX" },
];

export default function HRMSCnPayroll() {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  // 【2026-10-26 第四批】角色名单改读页面权限点 /hrms/cn-payroll（可配），区域判定保留
  const canManage = useCnPayrollAccess(user);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(TABS.some((t) => t.k === sp.get("tab")) ? sp.get("tab") : "runs");

  const loadCfg = useCallback(() => api.get("/hrms/cn-payroll/config").then(({ data }) => { setCfg(data); setErr(""); })
    .catch((e) => setErr(e?.detailText || T("加载失败", "Load failed"))), [api]); // eslint-disable-line
  useEffect(() => { if (canManage) loadCfg(); }, [canManage, loadCfg]);

  if (!canManage) return <div className="p-10 text-center text-slate-400 text-sm" data-testid="hrms-cn-denied">{T("中国薪酬社保仅中国公司 HR / 财务及管理层可访问", "China payroll is for China HR / finance and management only")}</div>;
  if (err) return <div className="p-10 text-center text-amber-600 text-sm" data-testid="hrms-cn-error">{err}</div>;
  if (!cfg) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="space-y-4" data-testid="hrms-cn-payroll-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800 flex items-center gap-2"><Landmark className="h-5 w-5 text-red-600" />{T("中国薪酬社保", "China Payroll & Social Insurance")}</h1>
          <p className="text-xs text-slate-500 mt-1" data-testid="hrms-cn-subtitle">
            {T(`中国公司（¥ CNY）员工独立核算：工资结构 · 五险一金（${cfg.city}模板）· 个税手填 · 中国版工资单 · 总部参考汇率 1 CNY ≈ $${Number(cfg.fxUsdPerCny).toFixed(4)}`,
              `Separate CNY payroll for China entity staff: wage structure · social insurance & housing fund (${cfg.city} template) · manual IIT · Chinese payslip · HQ reference FX 1 CNY ≈ $${Number(cfg.fxUsdPerCny).toFixed(4)}`)}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`hrms-cn-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs px-3.5 py-1.5 rounded-full font-semibold transition-colors ${tab === t.k ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{zh ? t.zh : t.en}</button>
          ))}
        </div>
      </div>
      {tab === "runs" && <CnRunsTab cfg={cfg} zh={zh} />}
      {tab === "profiles" && <CnProfilesTab cfg={cfg} zh={zh} />}
      {tab === "config" && <CnConfigTab cfg={cfg} zh={zh} onSaved={loadCfg} />}
    </div>
  );
}
